import React, { useState } from "react";
import { toast } from "sonner";
import { X, CalendarClock, Loader2, Copy } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/** Recruiter "Schedule Meet" — creates a real Cloudivo Meet room (scheduled_at) and invites
 * the candidate to it via the existing /meetings + /meetings/{id}/invite endpoints, so the
 * recruiter can act on a candidate immediately with a real live-video meeting, not just an
 * async AI mock interview. */
export default function ScheduleMeetModal({ candidate, onClose }) {
  const [title, setTitle] = useState(`Interview with ${candidate.name}`);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [scheduling, setScheduling] = useState(false);
  const [scheduled, setScheduled] = useState(null);

  const schedule = async () => {
    if (!date || !time) {
      toast.error("Please pick a date and time for the meeting");
      return;
    }
    const scheduledAt = new Date(`${date}T${time}`).toISOString();
    setScheduling(true);
    try {
      const { data: meeting } = await api.post("/meetings", { title: title.trim() || `Interview with ${candidate.name}`, meeting_type: "call", scheduled_at: scheduledAt });
      await api.post(`/meetings/${meeting.id}/invite`, { user_ids: [candidate.user_id] });
      setScheduled(meeting);
      toast.success(`Meeting scheduled with ${candidate.name}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to schedule meeting");
    } finally {
      setScheduling(false);
    }
  };

  const joinLink = scheduled ? `${window.location.origin}/meeting/join/${scheduled.join_code}` : "";

  const copyLink = () => {
    navigator.clipboard.writeText(joinLink);
    toast.success("Meeting link copied");
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4" data-testid="schedule-meet-modal">
      <div className="glass-panel rounded-2xl p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-white text-sm flex items-center gap-2"><CalendarClock className="w-4 h-4 text-emerald-300" /> Schedule Meet with {candidate.name}</h3>
          <button onClick={onClose} data-testid="schedule-meet-close-btn" className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        {!scheduled ? (
          <>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Meeting Title</label>
            <input
              value={title} onChange={(e) => setTitle(e.target.value)} data-testid="schedule-meet-title-input"
              className="w-full mt-1.5 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-emerald-400"
            />

            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Date</label>
                <input
                  type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="schedule-meet-date-input"
                  min={new Date().toISOString().slice(0, 10)}
                  className="w-full mt-1.5 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-emerald-400"
                />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Time</label>
                <input
                  type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="schedule-meet-time-input"
                  className="w-full mt-1.5 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-emerald-400"
                />
              </div>
            </div>

            <p className="text-[11px] text-slate-500 mt-3">{candidate.name} will get a notification with the join link, and it'll be created as a live Cloudivo Meet room.</p>

            <button
              onClick={schedule} disabled={scheduling} data-testid="schedule-meet-confirm-btn"
              className="w-full mt-4 flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90 disabled:opacity-60 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors"
            >
              {scheduling ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />} Schedule Meet
            </button>
          </>
        ) : (
          <div className="text-center py-2" data-testid="schedule-meet-success">
            <div className="w-12 h-12 rounded-2xl bg-emerald-400/15 border border-emerald-400/30 flex items-center justify-center mx-auto mb-3">
              <CalendarClock className="w-6 h-6 text-emerald-400" />
            </div>
            <p className="text-sm text-white font-semibold">Meeting scheduled with {candidate.name}!</p>
            <p className="text-xs text-slate-400 mt-1">{new Date(`${date}T${time}`).toLocaleString()}</p>
            <div className="flex items-center gap-2 mt-4 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
              <span className="flex-1 text-xs text-slate-300 truncate text-left">{joinLink}</span>
              <button onClick={copyLink} data-testid="schedule-meet-copy-link-btn" className="text-emerald-300 hover:text-emerald-200 shrink-0"><Copy className="w-3.5 h-3.5" /></button>
            </div>
            <button onClick={onClose} data-testid="schedule-meet-done-btn" className="mt-4 bg-white/5 hover:bg-white/10 text-white font-semibold text-sm px-6 py-2.5 rounded-lg">Done</button>
          </div>
        )}
      </div>
    </div>
  );
}
